import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { JwtAuthService } from '../../shared/services/auth/jwt-auth.service';

export interface WhatsappMediaPayload {
  number: string;
  caption?: string;
  media: string;
  fileName?: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class WhatsappService {
  constructor(private http: HttpClient, private jwtAuthService: JwtAuthService) { }

  /**
   * Envia mensagem de texto/JSON para a Evolution API.
   * @param to destinatário (número ou id)
   * @param message conteúdo da mensagem
   * @param enviarAssunto opcional
   */
  sendText(instance: string, to: string, message: string, enviarAssunto = false): Observable<any> {
    const url = `${this.jwtAuthService.apiUrl}/evolution/w/message/sendText/${instance}`;
    const body = { to, message, enviarAssunto };
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post(url, body, { headers }).pipe(catchError(this.handleError));
  }

  /**
   * Envia mídia (arquivos base64 ou url) para a Evolution API via JSON, conforme novo endpoint.
   * @param instance nome ou id da instância Evolution
   * @param payload objeto contendo os dados da mídia (number, caption, media, fileName, etc)
   */
  sendMedia(instance: string, payload: WhatsappMediaPayload): Observable<any> {
    const url = `${this.jwtAuthService.apiUrl}/evolution/message/sendMedia/${instance}`;
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post(url, payload, { headers }).pipe(catchError(this.handleError));
  }

  private createHeaders(apiKey?: string): HttpHeaders {
    let headers = new HttpHeaders();
    if (apiKey) {
      headers = headers.set('apikey', apiKey);
    }
    return headers;
  }

  private handleError(error: any): Observable<never> {
    const errorDetail = this.buildDetailedErrorMessage(error);
    console.error('Erro WhatsappService:', error, 'Detalhes:', errorDetail);
    return throwError(() => ({
      message: errorDetail.message,
      userMessage: errorDetail.userMessage,
      type: errorDetail.type,
      status: error.status,
      originalError: error
    }));
  }

  /**
   * Construir mensagem de erro detalhada baseada no tipo e status
   */
  private buildDetailedErrorMessage(error: any): any {
    let message = 'Erro desconhecido';
    let userMessage = 'Erro ao enviar mensagem';
    let type = 'UNKNOWN';

    if (error.error instanceof ErrorEvent) {
      // Erro de rede/cliente
      message = `Erro de rede: ${error.error.message}`;
      userMessage = 'Falha na conexão com o servidor. Verifique sua internet.';
      type = 'NETWORK_ERROR';
    } else if (error.status === 0) {
      message = 'Não foi possível conectar ao servidor';
      userMessage = 'Servidor indisponível. Tente novamente.';
      type = 'CONNECTION_ERROR';
    } else if (error.status === 400) {
      // Bad request
      const errorBody = error.error?.message || error.error?.error || '';
      message = `Requisição inválida: ${errorBody}`;
      userMessage = `Formato inválido: ${errorBody || 'verifique os dados enviados'}`;
      type = 'VALIDATION_ERROR';
    } else if (error.status === 401) {
      message = 'Não autorizado - sessão expirada ou API Key inválida';
      userMessage = 'Falha de autenticação. Reconecte a instância WhatsApp.';
      type = 'AUTH_ERROR';
    } else if (error.status === 403) {
      message = 'Acesso proibido';
      userMessage = 'Você não tem permissão para enviar por esta instância.';
      type = 'FORBIDDEN_ERROR';
    } else if (error.status === 404) {
      message = 'Recurso não encontrado - Instância ou endpoint inválido';
      userMessage = 'Instância WhatsApp não encontrada. Reconecte.';
      type = 'NOT_FOUND_ERROR';
    } else if (error.status === 408) {
      message = 'Timeout - Servidor demorou muito para responder';
      userMessage = 'Requisição expirada. Tente novamente.';
      type = 'TIMEOUT_ERROR';
    } else if (error.status === 429) {
      message = 'Limite de requisições excedido';
      userMessage = 'Muitas mensagens enviadas. Aguarde alguns segundos antes de tentar novamente.';
      type = 'RATE_LIMIT_ERROR';
    } else if (error.status === 500) {
      const errorBody = error.error?.message || error.error?.error || '';
      message = `Erro interno do servidor: ${errorBody}`;
      userMessage = 'Servidor com erro. Tente novamente.';
      type = 'SERVER_ERROR';
    } else if (error.status === 503) {
      message = 'Serviço indisponível';
      userMessage = 'Serviço temporariamente indisponível. Tente novamente.';
      type = 'SERVICE_UNAVAILABLE_ERROR';
    } else if (error.error && error.error.message) {
      message = error.error.message;
      userMessage = error.error.message;
      type = 'API_ERROR';
    } else if (error.message) {
      message = error.message;
      userMessage = error.message;
      type = 'GENERIC_ERROR';
    }

    return { message, userMessage, type, status: error.status };
  }
}
